<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12">
                <div class="form-group">
                    <label class="d-flex justify-content-between">
                      <span>Specification</span>
                      <span :class="{'text-success': availableSpecificationLetters > 0 && form.name.length >= 3, 'text-danger': availableSpecificationLetters === 0}">{{availableSpecificationLetters}}</span>
                    </label>
                    <input type="text" placeholder="Specify the work you did, for whom/what etc." v-model="form.name"
                           class="form-control form-control-sm" maxlength="50"/>
                    <span class="help is-danger" v-if="form.errors.has('name')"
                          v-text="form.errors.get('name')"></span>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-4">
                <div class="form-group">
                    <label>Amount</label>
                    <input placeholder="Amount of hours, days etc." v-model="form.count"
                           class="form-control form-control-sm" type="number" step="1" min="0" lang="en"/>
                    <span class="help is-danger" v-if="form.errors.has('count')"
                          v-text="form.errors.get('count')"></span>
                </div>
            </div>
            <div class="col-sm-4">
                <div class="form-group">
                    <label for="unitSelect">Unit</label>
                    <model-list-select :list="units" v-model="form.invoice_row_unit_id" option-value="id"
                                       option-text="slug"
                                       :custom-text="unitName"
                                       placeholder="Select unit">
                    </model-list-select>
                    <span class="help is-danger" v-if="form.errors.has('invoice_row_unit_id')"
                          v-text="form.errors.get('invoice_row_unit_id')"></span>
                </div>
            </div>
            <div class="col-sm-4">
                <div class="form-group">
                    <label>Price per unit</label>
                    <input placeholder="Price per hour/day etc." v-model="form.price"
                           class="form-control form-control-sm no-arrows" type="number" step="0.01" min="0" lang="en"/>
                    <span class="help is-danger" v-if="form.errors.has('price')"
                          v-text="form.errors.get('price')"></span>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-6">
                <div class="form-group">
                    <label for="formControlSelect2">VAT (%)</label>
                    <select id="formControlSelect2" placeholder="Only for companies outside of Sweden "
                            class="form-control form-control-sm"
                            v-model="form.vat">
                        <option v-for="vatOption in vatOptions" :value="vatOption">
                            {{ vatOption }}%
                        </option>
                    </select>
                    <span class="help is-danger" v-if="form.errors.has('vat')"
                          v-text="form.errors.get('vat')"></span>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-12">
                <p v-for="vatOption in vatOptions" v-show="form.vat === vatOption">
                    {{ `invoice.vat.options.vat_${vatOption}` | lang }}
                </p>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-6">
                <div class="form-group">
                    <label>Started at</label>
                    <input type="text" v-model="form.started_at"
                           id="start_date" ref="start_date"
                           class="form-control form-control-sm" autocomplete="off"/>
                    <span class="help is-danger"
                          v-if="form.errors.has('started_at')"
                          v-text="form.errors.get('started_at')"></span>
                </div>
            </div>
            <div class="col-sm-6">
                <div class="form-group">
                    <label>Ended at</label>
                    <input type="text" v-model="form.ended_at" id="end_date"
                           ref="end_date" class="form-control form-control-sm"
                           autocomplete="off"/>
                    <span class="help is-danger" v-if="form.errors.has('ended_at')"
                          v-text="form.errors.get('ended_at')"></span>
                </div>
            </div>
        </div>
        <button class="btn btn-primary btn-block" v-on:click="addInvoiceRow"
                :disabled="formInvalid || locked">
            <span v-if="invoiceRow">Updated</span>
            <span v-else>Add</span>
        </button>
    </div>
</template>

<script>
  import 'vue-search-select/dist/VueSearchSelect.css';
  import {ModelListSelect} from 'vue-search-select';
  import TForm from "./TForm";
  import flatpickr from 'flatpickr';

  export default {
    name: "InvoiceRowEditor",
    props: {
      invoice: {
        type: Object,
        required: true,
      },
      units: {
        type: Array,
        required: true,
      },
      invoiceRow: {
        type: Object,
        required: false,
      },
      nationality: {
        type: String,
        required: true
      }
    },
    computed: {
      formInvalid() {
        return this.form.name.length < 3 || this.form.count <= 0 || this.form.price <= 0 ||
          this.form.started_at.length < 3 || this.form.ended_at.length < 3;
      },
      availableSpecificationLetters() {
        return 50 - parseInt(this.form.name.length || 0);
      }
    },
    watch: {
      invoiceRow(newValue, oldValue) {
        if (newValue === undefined) {
          this.resetForm();
          return;
        }

        Object.keys(this.form).forEach((key, index) => {
          if (newValue.hasOwnProperty(key)) {
            this.form[key] = newValue[key];
          }
        });
      }
    },
    data() {
      return {
        locked: false,
        vatOptions: ['0', '6', '10', '12', '14', '15', '24', '25'],
        form: new TForm({
          name: '',
          count: '',
          price: '',
          vat: this.nationality === 'FI' ? '24' : '25',
          amount: '',
          started_at: '',
          ended_at: '',
          invoice_row_unit_id: 1,
          is_product: false,
        }, true),
      };
    },
    mounted() {
      this.prepareComponent();
    },
    methods: {
      unitName(unit){
        return window.Lang.get(`units.${unit.slug}`);
      },
      prepareComponent() {
        this.$nextTick(() => {
          flatpickr(this.$refs.start_date, {
            dateFormat: 'Y-m-d',
            time_24hr: true,
            minDate: '2017-01-01',
          });
          flatpickr(this.$refs.end_date, {
            dateFormat: 'Y-m-d',
            time_24hr: true,
            minDate: '2017-01-01',
          });
        });
      },
      resetForm() {
        this.form.reset();
      },
      lock() {
        this.locked = true;
      },
      unlock() {
        this.locked = false;
      },
      addInvoiceRow() {
        if (this.locked) {
          return;
        }
        this.lock();

        // Remove any whitespace from the price
        this.form.price = this.form.price.replace(/\s/g, '');

        let url = `/invoices/${this.invoice.id}/rows`;
        if (this.invoiceRow !== undefined) {
          url = `/invoices/${this.invoice.id}/rows/${this.invoiceRow.id}`;
        }
        this.form.post(url).then(data => {
          this.$emit('interface', data);
        }).catch(error => {
          this.$toasted.error(error.message);
        }).finally(() => {
          this.unlock();
        });
      },
    }, components: {
      ModelListSelect,
    },
  }
</script>

<style scoped>

</style>
